"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Bounding boxes use normalized [0..1] coordinates from the top-left of the
// image area. Points use the same convention.
type BoxColor = "cyan" | "pink";
type Box = { x: number; y: number; w: number; h: number; label: string; conf: number; color: BoxColor };
type Point = { x: number; y: number; color?: BoxColor; scale?: number };
type OutputRow = { color: BoxColor; text: string };

type TaskId = "detect" | "point" | "caption" | "query" | "ocr";

type Scene = {
  id: string;
  skill: TaskId;
  label: string;
  sublabel: string;
  image: string;
  query: string;
  caption: string;
  ocr: string[];
  vqa: { q: string; a: string };
  detect: { latency: number; query: string; boxes: Box[]; output: OutputRow[] };
  point: {
    latency: number;
    query: string;
    points: Point[];
    output: OutputRow[];
  };
};

const SCENES: Scene[] = [
  {
    id: "warehouse",
    skill: "detect",
    label: "Warehouse",
    sublabel: "manufacturing / logistics",
    image: "https://depot.moondream.ai/images/warehouse.jpeg",
    query: '"Misoriented box"',
    caption:
      "Industrial warehouse aisle with stacked cardboard cartons on metal racking. Some cartons show visible dents and crushing along the lower shelf.",
    ocr: ["LOT 4471-A", "FRAGILE", "THIS SIDE UP", "MAX 5 STACK", "EXP 2026/04"],
    vqa: {
      q: "How many cartons appear damaged?",
      a: "Two cartons show visible damage — one with a crushed corner on the upper-left shelf, one dented along the front face on the lower shelf.",
    },
    detect: {
      latency: 451,
      query: '"Misoriented box"',
      boxes: [
        { x: 0.7, y: 0.39, w: 0.28, h: 0.22, label: "Misoriented box", conf: 0.94, color: "cyan" },
      ],
      output: [{ color: "cyan", text: '{label: "Misoriented box", conf: 0.94}' }],
    },
    point: {
      latency: 312,
      query: '"barcode"',
      points: [
        { x: 0.18, y: 0.32, color: "cyan" },
        { x: 0.51, y: 0.61, color: "cyan" },
        { x: 0.78, y: 0.41, color: "cyan" },
      ],
      output: [
        { color: "cyan", text: "(0.18, 0.32)" },
        { color: "cyan", text: "(0.51, 0.61)" },
        { color: "cyan", text: "(0.78, 0.41)" },
      ],
    },
  },
  {
    id: "app",
    skill: "point",
    label: "App interface",
    sublabel: "computer use agents",
    image: "https://depot.moondream.ai/images/medical-ui.jpeg",
    query: '"Where to click to accept prior authorization"',
    caption:
      "Healthcare provider portal showing a multi-step Prior Authorization form. Patient and request information sections are filled in with form fields, and a primary 'Next' action sits in the bottom-right of the workspace.",
    ocr: [
      "Create Prior Authorization",
      "Patient Information",
      "Request Information",
      "Diagnosis and Clinical Information",
      "Save as Draft",
      "Cancel",
      "Next",
    ],
    vqa: {
      q: "Where to click to accept prior authorization?",
      a: "Click the blue 'Next' button in the bottom-right corner. It's styled as the primary action — solid fill, higher contrast than the adjacent 'Save as Draft' and 'Cancel' buttons — and advances the form to the next step.",
    },
    detect: {
      latency: 219,
      query: '"Where to click to accept prior authorization"',
      boxes: [
        { x: 0.866, y: 0.918, w: 0.118, h: 0.04, label: "button", conf: 0.98, color: "cyan" },
      ],
      output: [{ color: "cyan", text: '{label: "button", conf: 0.98}' }],
    },
    point: {
      latency: 184,
      query: '"Where to click to accept prior authorization"',
      points: [{ x: 0.925, y: 0.938, color: "cyan", scale: 3 }],
      output: [{ color: "cyan", text: "(0.93, 0.94)" }],
    },
  },
  {
    id: "broadcast",
    skill: "detect",
    label: "Advertising",
    sublabel: "logo detection",
    image: "https://depot.moondream.ai/images/broadcast.jpeg",
    query: '"State Farm logo"',
    caption:
      "Broadcast frame from an NBA game between the Golden State Warriors and Dallas Mavericks. Courtside signage, scorer's-table LED boards, and an on-screen scorebug are visible alongside players in motion.",
    ocr: [
      "State Farm",
      "prime",
      "TISSOT",
      "KIA",
      "MICHELOB ULTRA",
      "GET THE APP",
      "aws",
      "RIVALS WEEK",
      "GSW",
      "DAL",
      "1ST",
      "1:00",
    ],
    vqa: {
      q: "Which sponsor logos appear in this frame?",
      a: "Five brand logos are visible: State Farm on the courtside signage under the basket, Amazon Prime on the LED scorer's-table panel, Kia on the stanchion padding at left, Tissot on the shot-clock unit above the backboard, and the AWS RIVALS WEEK lockup in the lower scorebug.",
    },
    detect: {
      latency: 384,
      query: '"State Farm logo"',
      boxes: [
        { x: 0.241, y: 0.185, w: 0.134, h: 0.06, label: "State Farm Logo", conf: 0.97, color: "cyan" },
      ],
      output: [{ color: "cyan", text: '{label: "State Farm Logo", conf: 0.97}' }],
    },
    point: {
      latency: 252,
      query: '"State Farm logo"',
      points: [{ x: 0.305, y: 0.205, color: "cyan" }],
      output: [{ color: "cyan", text: "(0.31, 0.21)" }],
    },
  },
  {
    id: "aircraft",
    skill: "query",
    label: "Aircraft ID",
    sublabel: "classification / ISR",
    image: "https://depot.moondream.ai/images/f22.jpeg",
    query: '"What type of aircraft is this?"',
    caption:
      "Twin-engine fifth-generation fighter jet in afterburner against a dusk sky, captured from a rear three-quarter angle. Twin canted vertical stabilizers, diamond-shaped wing planform, and serrated nozzle edges are visible.",
    ocr: ["USAF", "TY", "AF"],
    vqa: {
      q: "What type of aircraft is this?",
      a: "An F-22 Raptor — a U.S. Air Force fifth-generation air-superiority fighter. Identifying features visible: twin canted vertical stabilizers, diamond-shaped wings with clipped tips, serrated 2D thrust-vectoring nozzles in afterburner, and a single-piece bubble canopy.",
    },
    detect: {
      latency: 412,
      query: '"aircraft"',
      boxes: [
        { x: 0.1, y: 0.32, w: 0.62, h: 0.3, label: "F-22 Raptor", conf: 0.96, color: "cyan" },
      ],
      output: [{ color: "cyan", text: '{label: "F-22 Raptor", conf: 0.96}' }],
    },
    point: {
      latency: 238,
      query: '"cockpit"',
      points: [{ x: 0.2, y: 0.4, color: "cyan" }],
      output: [{ color: "cyan", text: "(0.20, 0.40)" }],
    },
  },
  {
    id: "scene",
    skill: "detect",
    label: "Scene tagging",
    sublabel: "image / video metadata",
    image: "https://depot.moondream.ai/images/scene-tagging.jpeg",
    query: '"key items in scene"',
    caption:
      "Bright open-plan kitchen and adjoining home office. A granite-topped island with maroon cabinetry sits beneath two pendant lights; a desk with a lamp and laptop is visible through the doorway on the left, and woven rugs run along the right side.",
    ocr: [],
    vqa: {
      q: "What are the key items in this scene?",
      a: "Granite countertop, white upper cabinets, kitchen sink, two pendant light fixtures, two woven rugs, and a desk lamp in the home office through the doorway.",
    },
    detect: {
      latency: 478,
      query: '"key items in scene"',
      boxes: [
        { x: 0.2656, y: 0.5728, w: 0.5938, h: 0.1826, label: "Granite countertop", conf: 0.96, color: "cyan" },
        { x: 0.5508, y: 0.3271, w: 0.1797, h: 0.1504, label: "Cabinets", conf: 0.93, color: "cyan" },
        { x: 0.7847, y: 0.544, w: 0.1025, h: 0.0136, label: "Sink", conf: 0.86, color: "pink" },
        { x: 0.4976, y: 0.2048, w: 0.1611, h: 0.106, label: "Light fixture", conf: 0.94, color: "cyan" },
        { x: 0.6267, y: 0.2793, w: 0.1138, h: 0.0703, label: "Light fixture", conf: 0.92, color: "cyan" },
        { x: 0.8286, y: 0.8452, w: 0.1709, h: 0.1533, label: "Rug", conf: 0.89, color: "pink" },
        { x: 0.8438, y: 0.7358, w: 0.1484, h: 0.083, label: "Rug", conf: 0.87, color: "pink" },
        { x: 0.0454, y: 0.4663, w: 0.0811, h: 0.1143, label: "Desk lamp", conf: 0.91, color: "cyan" },
        { x: 0.7393, y: 0.3135, w: 0.248, h: 0.2207, label: "Window", conf: 0.95, color: "cyan" },
      ],
      output: [
        { color: "cyan", text: '{label: "Granite countertop", conf: 0.96}' },
        { color: "cyan", text: '{label: "Cabinets", conf: 0.93}' },
        { color: "pink", text: '{label: "Sink", conf: 0.86}' },
        { color: "cyan", text: '{label: "Light fixture", conf: 0.94}' },
        { color: "cyan", text: '{label: "Light fixture", conf: 0.92}' },
        { color: "pink", text: '{label: "Rug", conf: 0.89}' },
        { color: "pink", text: '{label: "Rug", conf: 0.87}' },
        { color: "cyan", text: '{label: "Desk lamp", conf: 0.91}' },
        { color: "cyan", text: '{label: "Window", conf: 0.95}' },
      ],
    },
    point: {
      latency: 312,
      query: '"light fixtures"',
      points: [
        { x: 0.578, y: 0.258, color: "cyan" },
        { x: 0.683, y: 0.314, color: "cyan" },
      ],
      output: [
        { color: "cyan", text: "(0.58, 0.26)" },
        { color: "cyan", text: "(0.68, 0.31)" },
      ],
    },
  },
];

type Device = {
  id: string;
  label: string;
  sub: string;
  detectBase: number;
  perItem: number;
  queryBase: number;
  meta: string;
};

const DEVICES: Device[] = [
  { id: "rtx6000", label: "Linux RTX 6000", sub: "workstation", detectBase: 32, perItem: 1, queryBase: 44, meta: "Moondream 3 Preview · Photon · Fine-tuned with Lens" },
  { id: "orin", label: "Linux Orin 32GB", sub: "edge · jetson", detectBase: 300, perItem: 10, queryBase: 403, meta: "Moondream 3 Preview · Photon · Fine-tuned with Lens" },
  { id: "h100", label: "Linux H100", sub: "datacenter", detectBase: 28, perItem: 1, queryBase: 38, meta: "Moondream 3 Preview · Photon · Fine-tuned with Lens" },
  { id: "a1000", label: "Linux A1000", sub: "entry pro", detectBase: 90, perItem: 3, queryBase: 132, meta: "Moondream 3 Preview · Photon · Fine-tuned with Lens" },
];

const TASK_LABEL: Record<TaskId, string> = {
  detect: "Detect",
  point: "Point",
  caption: "Caption",
  query: "Query",
  ocr: "OCR",
};

// ---------- Code header ----------
const CodeLine = ({ task, query, typing }: { task: TaskId; query: string; typing: boolean }) => (
  <div className="md-code">
    <span className="md-code-prompt">{">"}</span>
    <span>
      <span className="md-code-method">model</span>
      <span className="md-code-dot">.</span>
      <span className="md-code-method">{task}</span>
      <span className="md-code-paren">(</span>
      <span className="md-code-arg">{query}</span>
      <span className="md-code-paren">)</span>
      {typing && <span className="md-code-cursor" />}
    </span>
  </div>
);

// ---------- Latency pill (device picker) ----------
const LatencyPicker = ({
  ms,
  running,
  deviceId,
  onDevice,
}: {
  ms: number;
  running: boolean;
  deviceId: string;
  onDevice: (id: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const device = DEVICES.find((d) => d.id === deviceId) || DEVICES[0];
  return (
    <div className="md-latency-wrap" ref={ref}>
      <button
        type="button"
        className={
          "md-latency md-latency-btn" +
          (running ? " md-latency-running" : "") +
          (open ? " md-latency-open" : "")
        }
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="md-latency-dot" />
        <span className="md-latency-ms">{running ? "running…" : `${ms} ms`}</span>
        <span className="md-latency-sep">·</span>
        <span className="md-latency-device">{device.label}</span>
        <svg className="md-latency-caret" viewBox="0 0 8 5" fill="none" aria-hidden="true">
          <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="md-latency-menu" role="listbox">
          {DEVICES.map((d) => {
            const selected = d.id === deviceId;
            return (
              <button
                type="button"
                key={d.id}
                role="option"
                aria-selected={selected}
                className={"md-latency-opt" + (selected ? " md-latency-opt-active" : "")}
                onClick={() => {
                  onDevice(d.id);
                  setOpen(false);
                }}
              >
                <span className="md-latency-opt-check" aria-hidden="true">
                  {selected && (
                    <svg viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className="md-latency-opt-label">{d.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------- Run button ----------
const RunButton = ({ onClick, running }: { onClick: () => void; running: boolean }) => (
  <button
    type="button"
    className="md-run-btn"
    onClick={onClick}
    disabled={running}
    aria-label="Run again"
    title="Run again"
  >
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M13.5 2.5V5h-2.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

// ---------- Canvas ----------
const Canvas = ({
  scene,
  task,
  runKey,
  animateBoxes,
}: {
  scene: Scene;
  task: TaskId;
  runKey: number;
  animateBoxes: boolean;
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    // If the browser already finished loading the image before React attached
    // the onLoad handler (common on SSR hydration with a cached / fast-fetched
    // image), the load event is missed — sync state from the DOM instead.
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      setImgLoaded(true);
    } else {
      setImgLoaded(false);
    }
  }, [scene.id]);

  const detect = scene.detect;
  const point = scene.point;

  return (
    <div className="md-canvas">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        key={scene.id}
        ref={imgRef}
        className={"md-canvas-img" + (imgLoaded ? " loaded" : "")}
        src={scene.image}
        alt=""
        onLoad={() => setImgLoaded(true)}
        onError={() => setImgLoaded(true)}
      />
      <div className="md-canvas-stripes" />
      <div className="md-canvas-vignette" />

      {task === "detect" &&
        animateBoxes &&
        detect.boxes.map((b, i) => (
          <div
            key={`${runKey}-${i}`}
            className={"md-bbox" + (b.color === "pink" ? " md-bbox-pink" : "")}
            style={{
              left: `${b.x * 100}%`,
              top: `${b.y * 100}%`,
              width: `${b.w * 100}%`,
              height: `${b.h * 100}%`,
              animationDelay: `${i * 0.12}s`,
            }}
          >
            <div className="md-bbox-label">
              {b.label} {b.conf.toFixed(2)}
            </div>
          </div>
        ))}

      {task === "point" &&
        animateBoxes &&
        point.points.map((p, i) => (
          <div
            key={`${runKey}-pt-${i}`}
            className="md-point"
            style={{
              left: `${p.x * 100}%`,
              top: `${p.y * 100}%`,
              animationDelay: `${i * 0.12}s`,
              ...(p.scale ? ({ "--md-point-scale": p.scale } as React.CSSProperties) : {}),
            }}
          />
        ))}

      {task === "ocr" &&
        animateBoxes &&
        scene.ocr.map((t, i) => {
          const positions = [
            { x: 22, y: 25 },
            { x: 58, y: 32 },
            { x: 30, y: 55 },
            { x: 70, y: 60 },
            { x: 45, y: 78 },
            { x: 78, y: 18 },
            { x: 18, y: 78 },
          ];
          const pos = positions[i % positions.length];
          return (
            <div
              key={`${runKey}-ocr-${i}`}
              className="md-ocr-tag"
              style={{ left: `${pos.x}%`, top: `${pos.y}%`, animationDelay: `${i * 0.08}s` }}
            >
              {t}
            </div>
          );
        })}

      {(task === "caption" || task === "query") && animateBoxes && scene.id !== "aircraft" && (
        <div className="md-overlay-text">
          <div className="md-overlay-card">
            {task === "query" && <div className="md-overlay-card-q">Q · {scene.vqa.q}</div>}
            <div className="md-overlay-card-a">
              {task === "caption" ? scene.caption : scene.vqa.a}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Output panel ----------
const Output = ({ scene, task }: { scene: Scene; task: TaskId }) => {
  if (task === "detect") {
    return (
      <div className="md-output">
        {scene.detect.output.map((row, i) => (
          <div key={i} className="md-output-row">
            <span className={"md-output-chip" + (row.color === "pink" ? " md-output-chip-pink" : "")} />
            <span className="md-output-text">{row.text}</span>
          </div>
        ))}
      </div>
    );
  }
  if (task === "point") {
    return (
      <div className="md-output">
        {scene.point.output.map((row, i) => (
          <div key={i} className="md-output-row">
            <span className="md-output-chip" />
            <span className="md-output-text">{row.text}</span>
          </div>
        ))}
      </div>
    );
  }
  if (task === "caption") {
    return <p className="md-output-block">&ldquo;{scene.caption}&rdquo;</p>;
  }
  if (task === "query") {
    return (
      <div>
        <p className="md-output-q">Q · {scene.vqa.q}</p>
        <p className="md-output-block md-output-a">{scene.vqa.a}</p>
      </div>
    );
  }
  if (task === "ocr") {
    return (
      <div className="md-output-ocr">
        {scene.ocr.map((t, i) => (
          <span key={i} className="md-output-ocr-item">
            {t}
          </span>
        ))}
      </div>
    );
  }
  return null;
};

// ---------- Sidebar ----------
const Sidebar = ({
  sceneId,
  onScene,
}: {
  sceneId: string;
  onScene: (id: string) => void;
}) => (
  <aside className="md-sidebar">
    <p className="md-section-label">Scene</p>
    <div className="md-scene-list">
      {SCENES.map((s) => (
        <button
          type="button"
          key={s.id}
          className={"md-scene" + (s.id === sceneId ? " md-scene-active" : "")}
          onClick={() => onScene(s.id)}
        >
          <div className="md-scene-row">
            <span className="md-scene-label">{s.label}</span>
            <span className="md-scene-skill">{TASK_LABEL[s.skill]}</span>
          </div>
          <div className="md-scene-sub">{s.sublabel}</div>
        </button>
      ))}
    </div>
  </aside>
);

const hashJitter = (seed: string) => {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
};

export const InferenceDemo = () => {
  const [sceneId, setSceneId] = useState<string>(SCENES[0].id);
  const [deviceId, setDeviceId] = useState<string>("rtx6000");
  const [running, setRunning] = useState(false);
  const [animateBoxes, setAnimateBoxes] = useState(true);
  const [runKey, setRunKey] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);

  const scene = useMemo(
    () => SCENES.find((s) => s.id === sceneId) || SCENES[0],
    [sceneId],
  );
  const taskId: TaskId = scene.skill;
  const taskData = useMemo(() => {
    if (taskId === "detect") return scene.detect;
    if (taskId === "point") return scene.point;
    return null;
  }, [scene, taskId]);

  const device = DEVICES.find((d) => d.id === deviceId) || DEVICES[0];

  let displayLatency: number;
  if (taskId === "detect") {
    const n = scene.detect.boxes.length || 1;
    displayLatency = device.detectBase + device.perItem * n;
  } else if (taskId === "point") {
    const n = scene.point.points.length || 1;
    displayLatency = device.detectBase + device.perItem * n;
  } else {
    displayLatency = device.queryBase;
  }
  const jitterFrac = hashJitter(`${scene.id}|${taskId}|${device.id}|${runKey}`);
  const jitter = (jitterFrac - 0.5) * 0.06;
  displayLatency = Math.max(8, Math.round(displayLatency * (1 + jitter)));

  const displayQuery = taskData
    ? taskData.query
    : taskId === "query"
    ? `"${scene.vqa.q}"`
    : taskId === "ocr"
    ? '"all text"'
    : `"${scene.label || "scene"}"`;

  const cycleRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const interactRef = useRef(false);
  interactRef.current = userInteracted;

  // Auto-cycle scenes (until the user clicks something).
  useEffect(() => {
    cycleRef.current = setInterval(() => {
      if (interactRef.current) return;
      setSceneId((prev) => {
        const idx = SCENES.findIndex((s) => s.id === prev);
        return SCENES[(idx + 1) % SCENES.length].id;
      });
    }, 3800);
    return () => {
      if (cycleRef.current) clearInterval(cycleRef.current);
    };
  }, []);

  const runDurationRef = useRef(520);
  runDurationRef.current = Math.max(180, Math.min(900, displayLatency + 120));

  const triggerRun = useCallback(() => {
    setRunning(true);
    setAnimateBoxes(false);
    setRunKey((k) => k + 1);
    const t = setTimeout(() => {
      setRunning(false);
      setAnimateBoxes(true);
    }, runDurationRef.current);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const cleanup = triggerRun();
    return cleanup;
  }, [sceneId, deviceId, triggerRun]);

  const handleScene = (id: string) => {
    setUserInteracted(true);
    setSceneId(id);
  };
  const handleRunAgain = () => {
    setUserInteracted(true);
    triggerRun();
  };
  const handleDevice = (id: string) => {
    setUserInteracted(true);
    setDeviceId(id);
  };

  return (
    <div className="md-demo">
      <div className="md-card">
        <div className="md-header">
          <CodeLine task={taskId} query={displayQuery} typing={running} />
          <LatencyPicker
            ms={displayLatency}
            running={running}
            deviceId={deviceId}
            onDevice={handleDevice}
          />
          <RunButton onClick={handleRunAgain} running={running} />
        </div>
        <div className="md-rule" />
        <div className="md-progress" aria-hidden="true">
          <div
            key={runKey}
            className={"md-progress-bar" + (running ? " md-progress-bar-running" : "")}
            style={{ animationDuration: `${runDurationRef.current}ms` }}
          />
        </div>
        <div className="md-body">
          <div className="md-stage">
            <div className="md-canvas-wrap">
              <Canvas
                scene={scene}
                task={taskId}
                runKey={runKey}
                animateBoxes={animateBoxes}
              />
            </div>
            <div className="md-output-section">
              <p className="md-section-label">Output</p>
              <div className="md-output-scroll">
                <Output scene={scene} task={taskId} />
              </div>
            </div>
          </div>
          <Sidebar sceneId={sceneId} onScene={handleScene} />
        </div>
        <div className="md-card-foot">{device.meta}</div>
      </div>
    </div>
  );
};
